import React, { useState, useEffect, useMemo } from 'react';
import { 
  Paper, 
  Grid, 
  FormControl, 
  InputLabel, 
  Select, 
  MenuItem, 
  Button, 
  Box, 
  Typography,
  TextField,
  Checkbox,
  ListItemText,
  Chip,
  OutlinedInput
} from '@mui/material';
import { 
  FilterList as FilterIcon,
  Clear as ClearIcon 
} from '@mui/icons-material';
import { 
  getStates, 
  getPriorities 
} from '../../services/mockDataService';
import { getFixVersions, getTeams } from '../../services/apiService';

/**
 * FilterPanel component for filtering work items
 * @param {Object} props - Component props
 * @param {Function} props.onFilterChange - Callback when filters change
 * @param {Boolean} props.showTeamFilter - Whether to show team filter
 * @param {Boolean} props.showPriorityFilter - Whether to show priority filter
 * @param {Boolean} props.showStateFilter - Whether to show state filter
 * @param {Boolean} props.showFixVersionFilter - Whether to show fix version filter
 * @param {Boolean} props.showDateFilters - Whether to show date filters
 * @param {String} props.startDate - Start date for filtering (YYYY-MM-DD)
 * @param {String} props.endDate - End date for filtering (YYYY-MM-DD)
 * @param {Function} props.onStartDateChange - Callback when start date changes
 * @param {Function} props.onEndDateChange - Callback when end date changes
 * @param {Array} props.data - Data array to extract unique filter values from
 * @returns {JSX.Element} FilterPanel component
 */
const FilterPanel = ({ 
  onFilterChange, 
  showTeamFilter = true,
  showPriorityFilter = true,
  showStateFilter = true,
  showFixVersionFilter = true,
  showDateFilters = false,
  startDate = '2025-03-01',
  endDate = '2025-07-31',
  onStartDateChange = () => {},
  onEndDateChange = () => {},
  data = []
}) => {
  // Filter states
  const [state, setState] = useState([]);
  const [fixVersion, setFixVersion] = useState([]);
  const [priority, setPriority] = useState([]);
  const [team, setTeam] = useState([]);
  
  // Options for filters
  const [states, setStates] = useState([]);
  const [fixVersions, setFixVersions] = useState([]);
  const [priorities, setPriorities] = useState([]);
  const [teams, setTeams] = useState([]);
  
  // Loading states
  const [loading, setLoading] = useState(true);
  
  // Extract unique state values from data if provided
  const extractUniqueStates = (dataArray) => {
    const uniqueStates = new Set();
    dataArray.forEach(item => {
      if (item.state) {
        uniqueStates.add(item.state);
      }
    });
    return Array.from(uniqueStates).sort();
  };
  
  // Update states when data changes
  useEffect(() => {
    if (showStateFilter && data && data.length > 0) {
      const uniqueStates = extractUniqueStates(data);
      if (uniqueStates.length > 0) {
        setStates(uniqueStates);
      }
    }
  }, [data, showStateFilter]);
  
  // Fetch filter options on component mount
  useEffect(() => {
    const fetchFilterOptions = async () => {
      try {
        setLoading(true);
        
        // Don't set states here if we're getting them from data
        // Let the data useEffect handle it
        let statesData = [];
        if (!data || data.length === 0) {
          // Only fetch from mock service if no data is provided
          statesData = await getStates();
          setStates(statesData);
        }
        
        // Fetch other filter options in parallel
        const [fixVersionsResponse, prioritiesData, teamsResponse] = await Promise.all([
          getFixVersions(),
          getPriorities(),
          getTeams()
        ]);
        
        // Handle fix versions response structure
        if (fixVersionsResponse && fixVersionsResponse.data && Array.isArray(fixVersionsResponse.data)) {
          // Extract version strings from the fix version objects
          const versionStrings = fixVersionsResponse.data.map(fv => fv.version || fv);
          setFixVersions(versionStrings);
        } else {
          console.error('Invalid fix versions response:', fixVersionsResponse);
          setFixVersions([]); // Initialize with empty array to prevent map errors
        }
        
        setPriorities(prioritiesData);
        
        // Use all teams regardless of enabled status
        if (teamsResponse && teamsResponse.success && teamsResponse.data) {
          setTeams(teamsResponse.data);
        } else {
          console.error('Invalid teams response:', teamsResponse);
          setTeams([]); // Initialize with empty array to prevent map errors
        }
      } catch (error) {
        console.error('Error fetching filter options:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchFilterOptions();
  }, []); // Only run once on mount
  
  // Create memoized filters object
  // Using OR condition for filter values (multiple selections are treated as OR)
  const filters = useMemo(() => ({
    ...(state.length > 0 && { state: state.join(',') }), // Using comma for OR condition (backend expects comma-separated values)
    ...(fixVersion.length > 0 && { fixVersion: fixVersion.join(',') }),
    ...(priority.length > 0 && { priority: priority.join(',') }),
    ...(team.length > 0 && { team: team.join(',') })
  }), [state, fixVersion, priority, team]);
  
  // Add a comment to document the OR condition implementation
  // This ensures that when multiple values are selected in a filter,
  // items matching ANY of the selected values will be shown (OR condition)
  
  // Apply filters when they change
  useEffect(() => {
    onFilterChange(filters);
  }, [filters, onFilterChange]);
  
  // Reset all filters
  const handleReset = () => {
    setState([]);
    setFixVersion([]);
    setPriority([]);
    setTeam([]);
  };
  
  // Clear specific filter
  const handleClearFilter = (filterName) => {
    switch(filterName) {
      case 'state':
        setState([]);
        break;
      case 'fixVersion':
        setFixVersion([]);
        break;
      case 'priority':
        setPriority([]);
        break;
      case 'team':
        setTeam([]);
        break;
      default:
        break;
    }
  };
  
  return (
    <Paper style={{ padding: 16, marginBottom: 16 }}>
      <Box display="flex" alignItems="center" marginBottom={2}>
        <FilterIcon style={{ marginRight: 8 }} />
        <Typography variant="h6">Filters</Typography>
      </Box>
      
      <Grid container spacing={2}>
        {showStateFilter && (
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth variant="outlined" size="small">
              <InputLabel id="state-filter-label">State</InputLabel>
              <Select
                labelId="state-filter-label"
                id="state-filter"
                multiple
                value={state}
                onChange={(e) => setState(e.target.value)}
                input={<OutlinedInput label="State" />}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map((value) => (
                      <Chip key={value} label={value} size="small" />
                    ))}
                  </Box>
                )}
                disabled={loading || states.length === 0}
                MenuProps={{
                  PaperProps: {
                    style: {
                      maxHeight: 48 * 4.5 + 8,
                      width: 250,
                    },
                  },
                }}
              >
                {state.length > 0 && (
                  <MenuItem onClick={() => handleClearFilter('state')}>
                    <em>Clear Selection</em>
                  </MenuItem>
                )}
                {states.length === 0 ? (
                  <MenuItem disabled>
                    <ListItemText primary="Loading states..." />
                  </MenuItem>
                ) : (
                  states.map((stateOption) => (
                    <MenuItem key={stateOption} value={stateOption}>
                      <Checkbox checked={state.indexOf(stateOption) > -1} />
                      <ListItemText primary={stateOption} />
                    </MenuItem>
                  ))
                )}
              </Select>
            </FormControl>
          </Grid>
        )}
        
        {showFixVersionFilter && (
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth variant="outlined" size="small">
              <InputLabel id="fix-version-filter-label">Fix Version</InputLabel>
              <Select
                labelId="fix-version-filter-label"
                id="fix-version-filter"
                multiple
                value={fixVersion}
                onChange={(e) => setFixVersion(e.target.value)}
                input={<OutlinedInput label="Fix Version" />}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map((value) => (
                      <Chip key={value} label={value} size="small" />
                    ))}
                  </Box>
                )}
                disabled={loading}
                MenuProps={{
                  PaperProps: {
                    style: {
                      maxHeight: 48 * 4.5 + 8,
                      width: 250,
                    },
                  },
                }}
              >
                {fixVersion.length > 0 && (
                  <MenuItem onClick={() => handleClearFilter('fixVersion')}>
                    <em>Clear Selection</em>
                  </MenuItem>
                )}
                {fixVersions.map((versionOption) => (
                  <MenuItem key={versionOption} value={versionOption}>
                    <Checkbox checked={fixVersion.indexOf(versionOption) > -1} />
                    <ListItemText primary={versionOption} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        )}
        
        {showPriorityFilter && (
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth variant="outlined" size="small">
              <InputLabel id="priority-filter-label">Priority</InputLabel>
              <Select
                labelId="priority-filter-label"
                id="priority-filter"
                multiple
                value={priority}
                onChange={(e) => setPriority(e.target.value)}
                input={<OutlinedInput label="Priority" />}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map((value) => (
                      <Chip key={value} label={`P${value}`} size="small" />
                    ))}
                  </Box>
                )}
                disabled={loading}
                MenuProps={{
                  PaperProps: {
                    style: {
                      maxHeight: 48 * 4.5 + 8,
                      width: 250,
                    },
                  },
                }}
              >
                {priority.length > 0 && (
                  <MenuItem onClick={() => handleClearFilter('priority')}>
                    <em>Clear Selection</em>
                  </MenuItem>
                )}
                {priorities.map((priorityOption) => (
                  <MenuItem key={priorityOption} value={priorityOption}>
                    <Checkbox checked={priority.indexOf(priorityOption) > -1} />
                    <ListItemText primary={`P${priorityOption}`} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        )}
        
        {showTeamFilter && (
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth variant="outlined" size="small">
              <InputLabel id="team-filter-label">Team</InputLabel>
              <Select
                labelId="team-filter-label"
                id="team-filter"
                multiple
                value={team}
                onChange={(e) => setTeam(e.target.value)}
                input={<OutlinedInput label="Team" />}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map((value) => {
                      const teamObj = teams.find(t => (t.id || t) === value);
                      const displayName = teamObj ? (teamObj.displayName || teamObj) : value;
                      return <Chip key={value} label={displayName} size="small" />;
                    })}
                  </Box>
                )}
                disabled={loading}
                MenuProps={{
                  PaperProps: {
                    style: {
                      maxHeight: 48 * 4.5 + 8,
                      width: 250,
                    },
                  },
                }}
              >
                {team.length > 0 && (
                  <MenuItem onClick={() => handleClearFilter('team')}>
                    <em>Clear Selection</em>
                  </MenuItem>
                )}
                {teams.map((teamOption) => (
                  <MenuItem key={teamOption.id || teamOption} value={teamOption.id || teamOption}>
                    <Checkbox checked={team.indexOf(teamOption.id || teamOption) > -1} />
                    <ListItemText primary={teamOption.displayName || teamOption} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        )}
        
        {showDateFilters && (
          <>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth variant="outlined" size="small">
                <InputLabel shrink htmlFor="start-date-filter">Start Date</InputLabel>
                <TextField
                  id="start-date-filter"
                  type="date"
                  value={startDate}
                  onChange={(e) => onStartDateChange(e.target.value)}
                  fullWidth
                  InputLabelProps={{
                    shrink: true,
                  }}
                  size="small"
                />
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth variant="outlined" size="small">
                <InputLabel shrink htmlFor="end-date-filter">End Date</InputLabel>
                <TextField
                  id="end-date-filter"
                  type="date"
                  value={endDate}
                  onChange={(e) => onEndDateChange(e.target.value)}
                  fullWidth
                  InputLabelProps={{
                    shrink: true,
                  }}
                  size="small"
                />
              </FormControl>
            </Grid>
          </>
        )}
        
        <Grid item xs={12} sm={6} md={3}>
          <Button
            variant="outlined"
            color="secondary"
            startIcon={<ClearIcon />}
            onClick={handleReset}
            disabled={loading || (state.length === 0 && fixVersion.length === 0 && priority.length === 0 && team.length === 0)}
            fullWidth
          >
            Clear Filters
          </Button>
        </Grid>
      </Grid>
    </Paper>
  );
};

export default FilterPanel;
